import React, { useEffect, useRef, useState } from 'react';
import Window from '../os/Window';
import eatMp3 from '../../assets/audio/high.mp3';
import dieMp3 from '../../assets/audio/die.mp3';

export interface SnakeAppProps extends WindowAppProps {}

type Point = { x: number; y: number };
type Direction = 'UP' | 'DOWN' | 'LEFT' | 'RIGHT';

// Dimensioni più grandi, stile altri giochi
const GRID_SIZE = 20;
const CELL_SIZE = 32; // pixels -> canvas 640x640
const TICK_MS = 120; // velocità vicina all'originale

const GREEN = '#2aff2a';
const DARK_GREEN = '#0f5e0f';
const BLACK = '#000000';

const SnakeApp: React.FC<SnakeAppProps> = (props) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const [running, setRunning] = useState(true);
    const [score, setScore] = useState(0);
    const [gameOver, setGameOver] = useState(false);

    // Audio refs
    const eatAudioRef = useRef<HTMLAudioElement | null>(null);
    const dieAudioRef = useRef<HTMLAudioElement | null>(null);
    const audioUnlockedRef = useRef<boolean>(false);

    const [direction, setDirection] = useState<Direction>('RIGHT');
    const [nextDirection, setNextDirection] = useState<Direction>('RIGHT');
    const [snake, setSnake] = useState<Point[]>([
        { x: 5, y: 12 },
        { x: 4, y: 12 },
        { x: 3, y: 12 },
    ]);
    const [apple, setApple] = useState<Point>({ x: 16, y: 12 });

    // Initialize audio elements once
    useEffect(() => {
        eatAudioRef.current = new Audio(eatMp3);
        dieAudioRef.current = new Audio(dieMp3);
        if (eatAudioRef.current) eatAudioRef.current.volume = 0.6;
        if (dieAudioRef.current) dieAudioRef.current.volume = 0.7;
    }, []);

    // Keyboard controls
    useEffect(() => {
        const unlockAudio = () => {
            if (!audioUnlockedRef.current) {
                audioUnlockedRef.current = true;
                eatAudioRef.current?.play()
                    .then(() => {
                        if (eatAudioRef.current) {
                            eatAudioRef.current.pause();
                            eatAudioRef.current.currentTime = 0;
                        }
                    })
                    .catch(() => {});
                dieAudioRef.current?.play()
                    .then(() => {
                        if (dieAudioRef.current) {
                            dieAudioRef.current.pause();
                            dieAudioRef.current.currentTime = 0;
                        }
                    })
                    .catch(() => {});
            }
        };

        const onKey = (e: KeyboardEvent) => {
            unlockAudio();
            // Unlock audio on first user interaction to satisfy autoplay policies
            if (e.key === 'ArrowUp' && direction !== 'DOWN') setNextDirection('UP');
            else if (e.key === 'ArrowDown' && direction !== 'UP') setNextDirection('DOWN');
            else if (e.key === 'ArrowLeft' && direction !== 'RIGHT') setNextDirection('LEFT');
            else if (e.key === 'ArrowRight' && direction !== 'LEFT') setNextDirection('RIGHT');
            else if (e.key.toLowerCase() === 'p') setRunning((r) => !r);
            else if (e.key.toLowerCase() === 'r') restart();
        };
        const onMouseDown = () => unlockAudio();
        window.addEventListener('keydown', onKey);
        window.addEventListener('mousedown', onMouseDown);
        return () => {
            window.removeEventListener('keydown', onKey);
            window.removeEventListener('mousedown', onMouseDown);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [direction]);

    // Game loop
    useEffect(() => {
        const id = setInterval(() => {
            if (!running) return;
            setDirection(nextDirection);
            setSnake((prev) => {
                const head = { ...prev[0] };
                if (nextDirection === 'UP') head.y -= 1;
                if (nextDirection === 'DOWN') head.y += 1;
                if (nextDirection === 'LEFT') head.x -= 1;
                if (nextDirection === 'RIGHT') head.x += 1;

                // collisione con i muri (comportamento originale)
                if (head.x < 0 || head.x >= GRID_SIZE || head.y < 0 || head.y >= GRID_SIZE) {
                    // wall collision -> die sound
                    if (dieAudioRef.current) {
                        dieAudioRef.current.currentTime = 0;
                        dieAudioRef.current.play().catch(() => {});
                    }
                    setGameOver(true);
                    setRunning(false);
                    return prev;
                }

                const newSnake = [head, ...prev];

                // apple collision
                if (head.x === apple.x && head.y === apple.y) {
                    // eat sound
                    if (eatAudioRef.current) {
                        eatAudioRef.current.currentTime = 0;
                        eatAudioRef.current.play().catch(() => {});
                    }
                    setScore((s) => s + 10);
                    setApple(randomApple(newSnake));
                } else {
                    newSnake.pop();
                }

                // self collision -> game over
                if (newSnake.slice(1).some((p) => p.x === head.x && p.y === head.y)) {
                    // self collision -> die sound
                    if (dieAudioRef.current) {
                        dieAudioRef.current.currentTime = 0;
                        dieAudioRef.current.play().catch(() => {});
                    }
                    setGameOver(true);
                    setRunning(false);
                    return prev;
                }

                return newSnake;
            });
        }, TICK_MS);
        return () => clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [running, nextDirection, apple]);

    // ridisegna ad ogni cambiamento di stato del gioco
    useEffect(() => {
        draw();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [snake, apple, running, gameOver]);

    const randomApple = (occupied: Point[]): Point => {
        while (true) {
            const p = {
                x: Math.floor(Math.random() * GRID_SIZE),
                y: Math.floor(Math.random() * GRID_SIZE),
            };
            if (!occupied.some((o) => o.x === p.x && o.y === p.y)) return p;
        }
    };

    const restart = () => {
        setSnake([
            { x: 5, y: 12 },
            { x: 4, y: 12 },
            { x: 3, y: 12 },
        ]);
        setDirection('RIGHT');
        setNextDirection('RIGHT');
        setApple({ x: 16, y: 12 });
        setScore(0);
        setGameOver(false);
        setRunning(true);
    };

    const drawGrid = (ctx: CanvasRenderingContext2D) => {
        ctx.strokeStyle = DARK_GREEN;
        ctx.lineWidth = 1;
        for (let i = 0; i <= GRID_SIZE; i++) {
            ctx.beginPath();
            ctx.moveTo(i * CELL_SIZE + 0.5, 0);
            ctx.lineTo(i * CELL_SIZE + 0.5, GRID_SIZE * CELL_SIZE);
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(0, i * CELL_SIZE + 0.5);
            ctx.lineTo(GRID_SIZE * CELL_SIZE, i * CELL_SIZE + 0.5);
            ctx.stroke();
        }
    };

    const draw = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        // background
        ctx.fillStyle = BLACK;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        // grid
        drawGrid(ctx);
        // apple
        ctx.fillStyle = GREEN;
        ctx.fillRect(apple.x * CELL_SIZE, apple.y * CELL_SIZE, CELL_SIZE, CELL_SIZE);
        // snake
        ctx.fillStyle = GREEN;
        snake.forEach((p, i) => {
            // slight head glow
            if (i === 0) {
                ctx.shadowColor = GREEN;
                ctx.shadowBlur = 8;
            } else {
                ctx.shadowBlur = 0;
            }
            ctx.fillRect(p.x * CELL_SIZE, p.y * CELL_SIZE, CELL_SIZE, CELL_SIZE);
        });
        ctx.shadowBlur = 0;

        // Game Over screen
        if (gameOver) {
            ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            ctx.fillStyle = GREEN;
            ctx.shadowColor = GREEN;
            ctx.shadowBlur = 20;
            ctx.font = 'bold 48px Terminal';
            ctx.textAlign = 'center';
            ctx.fillText('GAME OVER', canvas.width / 2, canvas.height / 2 - 40);

            ctx.font = '24px Terminal';
            ctx.fillText(`Final Score: ${score}`, canvas.width / 2, canvas.height / 2 + 20);

            ctx.font = '18px Terminal';
            ctx.shadowBlur = 10;
            ctx.fillText('Press R to Restart', canvas.width / 2, canvas.height / 2 + 60);

            ctx.shadowBlur = 0;
            ctx.textAlign = 'left';
        }
    };

    return (
        <Window
            top={10}
            left={10}
            width={920}
            height={750}
            windowBarIcon="windowGameIcon"
            windowTitle="Snake"
            closeWindow={props.onClose}
            onInteract={props.onInteract}
            minimizeWindow={props.onMinimize}
            bottomLeftText={'© Snake Retro Mode'}
        >
            <div className="site-page" style={styles.container}>
                <div style={styles.header}>
                    <p style={styles.headerText}>
                        SCORE: {score} — Arrows: move, P: pausa, R: reset
                    </p>
                </div>
                <div style={styles.crtFrame}>
                    <canvas
                        ref={canvasRef}
                        width={GRID_SIZE * CELL_SIZE}
                        height={GRID_SIZE * CELL_SIZE}
                        style={styles.canvas}
                    />
                    <div style={styles.crtOverlay} />
                </div>
            </div>
        </Window>
    );
};

const styles: StyleSheetCSS = {
    container: {
        justifyContent: 'center',
        alignItems: 'center',
        padding: 12,
        backgroundColor: BLACK,
        color: GREEN,
        fontFamily: 'Terminal',
    },
    header: {
        width: '100%',
        alignItems: 'center',
        marginBottom: 8,
    },
    headerText: {
        color: GREEN,
        fontSize: 12,
        textShadow: `0 0 8px ${GREEN}`,
    },
    crtFrame: {
        position: 'relative',
        border: `2px solid ${DARK_GREEN}`,
        boxShadow: `0 0 12px ${GREEN}`,
        backgroundColor: BLACK,
        width: GRID_SIZE * CELL_SIZE,
        height: GRID_SIZE * CELL_SIZE,
    },
    canvas: {
        imageRendering: 'pixelated',
        display: 'block',
        width: GRID_SIZE * CELL_SIZE,
        height: GRID_SIZE * CELL_SIZE,
    },
    crtOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        backgroundImage:
            'repeating-linear-gradient(0deg, rgba(0,255,0,0.06) 0px, rgba(0,255,0,0.06) 1px, transparent 3px, transparent 4px)',
        mixBlendMode: 'screen',
        opacity: 0.8,
    },
};

export default SnakeApp;